import Box from '@mui/material/Box';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import config from '../../../config';
import SectionShareModal from '../../../containers/ListingPage/SectionShareModal';
import BookingDatesForm from '../../../forms/BookingDatesForm/BookingDatesForm';
import { diffTodayAndDate } from '../../../forms/BookingDatesForm/BookingDatesForm.steps';
import BookingModal from '../../../forms/BookingDatesForm/BookingModal';
import { hasOpenBookingLimit } from '../../../util/annualPass';
import useAppSelector from '../../../util/hooks/useAppSelector';
import useAppTheme from '../../../util/hooks/useAppTheme';
import usePass from '../../../util/hooks/usePass';
import useScreenSize from '../../../util/hooks/useScreenSize';
import { isCordova } from '../../../util/mobile/deviceDetection';
import { LISTING_STATE_CLOSED } from '../../../util/types';
import { parse } from '../../../util/urlHelpers';
import { bottomBarInset } from '../../Layouts/BottomNavigationBar/bottomBarInset';
import { getBookingModalVariant } from './BookingPanel.helpers';
import BookingPanelContent from './BookingPanelContent';
import useBookingDialogs from './useBookingDialogs';
import useBookingForm from './useBookingForm';
import useBookingPreview from './useBookingPreview';

// Parse ?checkIn=YYYY-MM-DD&checkOut=YYYY-MM-DD into a [moment, moment] tuple
// that BookingDatesForm can consume. Returns null if either date is missing,
// malformed, or if checkOut is not after checkIn.
const parseInitialDatesFromSearch = (search) => {
  const { checkIn, checkOut } = parse(search);
  if (typeof checkIn !== 'string' || typeof checkOut !== 'string') return null;
  const start = moment(checkIn, 'YYYY-MM-DD', true);
  const end = moment(checkOut, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
  return [start, end];
};

const ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT = config.annualPass.simultaneouslyBookingLimit;

const baseBoxStyle = {
  right: 0,
  zIndex: 2,
  backgroundColor: 'white',
  boxShadow: '0px 2px 16px rgba(0,0,0,0.06)',
  border: '1px solid #EFEFEF',
};
const mobileBoxStyle = {
  ...baseBoxStyle,
  position: 'fixed',
  bottom: 0,
  left: 0,
  transition: 'padding-bottom 0.3s ease',
  // Clear the BottomNav and its raised centre action, with the same gap as above the content.
  paddingBottom: (theme) => `calc(${bottomBarInset} + ${theme.spacing(2)})`,
};

// The bar leaves less room below while a tablet scrolls down.
const barBoxSx = {
  up: mobileBoxStyle,
  down: { ...mobileBoxStyle, paddingBottom: '44px' },
};

const desktopBoxStyle = {
  ...baseBoxStyle,
  position: 'absolute',
  height: 'fit-content',
  top: 0,
  left: 0,
  right: 0,
  p: 3,
  borderRadius: 2,
};

const sidebarContainerSx = {
  position: 'relative',
  width: '100%',
  height: '100%',
};

const WEB_TOPBAR_HEIGHT = 72;

/**
 * Height the desktop TopBar takes from the viewport. The app never shows a TopBar.
 * @param {boolean} inApp
 * @returns {number}
 */
export const getTopbarOffset = (inApp) => (inApp ? 0 : WEB_TOPBAR_HEIGHT);

/**
 * Whether the panel is too tall to follow the scroll inside the visible viewport.
 * @param {number} panelHeight
 * @param {number} viewportHeight
 * @param {number} bottomNavHeight - BottomNav covering the viewport bottom, 0 without one.
 * @returns {boolean}
 */
const exceedsViewport = (panelHeight, viewportHeight, bottomNavHeight) =>
  panelHeight > viewportHeight - bottomNavHeight;

const resetPanelStyles = (panel) => {
  panel.style.position = '';
  panel.style.top = '';
  panel.style.left = '';
  panel.style.width = '';
};

/**
 * Layout frame of the panel: the bar is fixed to the viewport bottom, the sidebar card sits in
 * the page column, where the desktop scroll effect positions `panelRef`. The content is its
 * direct child in both layouts.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} props.isTablet
 * @param {React.RefObject<HTMLElement>} props.panelRef - Sidebar card
 * @param {'bar' | 'sidebar'} props.variant
 */
const BookingPanelFrame = ({ children, isTablet, panelRef, variant }) => {
  const [scrollDirection, setScrollDirection] = useState('up');

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      if (isTablet) {
        if (window.scrollY > lastScrollY) {
          setScrollDirection('down');
        } else {
          setScrollDirection('up');
        }
      }
      lastScrollY = window.scrollY <= 0 ? 0 : window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isTablet]);

  if (variant === 'bar') {
    return (
      <Box px={2} py={2} sx={barBoxSx[scrollDirection]}>
        {children}
      </Box>
    );
  }

  return (
    <Box sx={sidebarContainerSx}>
      <Box ref={panelRef} sx={desktopBoxStyle}>
        {children}
      </Box>
    </Box>
  );
};

const BookingPanelComponent = ({
  distanceToLocation,

  // Listing data and status
  currentListing,
  category,
  isOwnListing = false,
  authorDisplayName = '',

  // Pricing and booking details
  formattedPrice,
  priceTitle,
  bookingLimit = null,
  minimumNights = 1,
  voucher = null,
  voucherValidationInProgress = false,
  voucherValidationError = null,

  // User and booking information
  toManyTransactionsWithProvider = false,

  // Reviews and social interaction
  onShareListing,

  // Booking and listing management
  onSubmit,
  onFetchTransactionLineItems,

  // Timeslots and availability
  timeSlots = null,
  onFetchTimeSlots,
  fetchTimeSlotsInProgress = false,

  // Financial elements and transactions
  lineItems = [],
  fetchLineItemsInProgress = false,

  // Other UI elements and states
  title,
  openShareModal = false,

  // Scrolling Effect Target
  startTargetRef,
  scrollTargetRef,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const [passIsValid, pass] = usePass();
  const panelRef = useRef(null);

  // URL deep-link support for the route planner's "Book now" CTA and any
  // other caller that wants to land users in the booking flow with dates
  // pre-filled. Parsed once per location.search change.
  const initialDates = useMemo(() => parseInitialDatesFromSearch(location.search), [location.search]);
  const autoOpenModal = useMemo(
    () => Boolean(parse(location.search).book),
    [location.search],
  );
  const currentUserOpenOrdersCount = useAppSelector((state) => state.user.currentUserOpenOrdersCount);

  const { isMobile, isTablet, width } = useScreenSize();
  const theme = useAppTheme();
  const inApp = isCordova();
  const isDesktopLayout = !(isTablet || isMobile);
  const variant = isDesktopLayout ? 'sidebar' : 'bar';
  const modalVariant = getBookingModalVariant(width, theme.breakpoints.values.sm);
  const topbarOffset = getTopbarOffset(inApp);
  // Inside the app the BottomNav stays visible at every width and covers the viewport bottom.
  const bottomNavHeight = inApp ? parseInt(theme.tokens.BottomBar_height, 10) : 0;

  const toManyOpenOrdersAsAnnualPassUser =
    hasOpenBookingLimit(pass) &&
    passIsValid &&
    currentUserOpenOrdersCount >= ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT;
  const showAnnualPassError =
    process.env.REACT_APP_ENV === 'production' &&
    ((toManyOpenOrdersAsAnnualPassUser &&
      intl.formatMessage({ id: 'BookingPanel.toManyOpenOrdersAsAnnualPassUser' })) ||
      (toManyTransactionsWithProvider &&
        intl.formatMessage({ id: 'BookingPanel.toManyTransactionsWithProvider' })));

  const hasListingState = !!currentListing.attributes.state;
  const isClosed = hasListingState && currentListing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingDatesForm = !isClosed;

  // The booking state lives here, so a layout switch only swaps the markup below.
  const { onBookingDataChange, onApplyVoucher } = useBookingPreview({
    listingId: currentListing.id,
    isOwnListing,
    onFetchTransactionLineItems,
  });
  const { sheetOpen, openSheet, closeSheet, checkoutOpen, openCheckout, closeCheckout } =
    useBookingDialogs({
      variant,
      autoOpen: autoOpenModal,
      hasInitialDates: Boolean(initialDates),
      scrollToTopOnOpen: isOwnListing || isClosed,
    });
  // Closing the sheet releases its scroll lock before the checkout page opens.
  const handleSubmit = useCallback(
    (values) => {
      closeSheet();
      onSubmit(values);
    },
    [closeSheet, onSubmit],
  );
  const bookingForm = useBookingForm({
    currentListing,
    initialDates,
    showAnnualPassError,
    appliedVoucher: voucher,
    voucherValidationError,
    voucherValidationInProgress,
    fetchLineItemsInProgress,
    onBookingDataChange,
    onApplyVoucher,
    onSubmit: handleSubmit,
  });
  const onMonthChange = useCallback(
    (date) => onFetchTimeSlots(diffTodayAndDate(date)),
    [onFetchTimeSlots],
  );

  useEffect(() => {
    if (!isDesktopLayout) return;

    const panel = panelRef.current;
    const padding = 16;
    const sectionSeparator = 16;
    const marginBottom = 32;

    // Set the start values for the scroll effect
    let topOfStartTarget = startTargetRef?.current?.getBoundingClientRect().top + window.scrollY;
    let topOfScrollTarget = scrollTargetRef?.current?.getBoundingClientRect().top + window.scrollY;

    const repositionPanel = () => {
      if (panelRef.current && startTargetRef.current && scrollTargetRef.current) {
        // Read all geometry before the first style write. A read between the writes forces a layout
        // of a half-written panel (absolute with the fixed-mode `left`), which overflows the page
        // to the right; the Android WebView then clamps the scroll range and touch scrolling ends early.
        const panelHeight = panelRef.current.offsetHeight;
        const startWidth = startTargetRef.current.offsetWidth;
        const startLeft = startTargetRef.current.offsetLeft;
        const currentScrollY = window.scrollY;
        // ---> ______
        //      |    |
        //      |    |

        // If the panel top is crossing the top of the section, make it fixed
        // going from top to bottom
        if (currentScrollY >= topOfStartTarget - topbarOffset) {
          panelRef.current.style.position = 'fixed';
          panelRef.current.style.top = `${topbarOffset + padding}px`;
          panelRef.current.style.width = `${startWidth}px`;
          panelRef.current.style.left = `${startLeft}px`;
        }

        // If the panel top is crossing the top of the section, make it absolute
        // going from bottom to top
        if (currentScrollY <= topOfStartTarget - topbarOffset) {
          panelRef.current.style.position = 'absolute';
          panelRef.current.style.width = '100%';
          panelRef.current.style.top = '0';
          panelRef.current.style.left = '0';
        }

        //      |    |
        //      |    |
        // ---> ------

        // If the panel bottom is crossing the bottom of the target section, we have to absolute the panel position there
        // going from top to bottom
        if (
          currentScrollY + panelHeight + topbarOffset + padding >=
          topOfScrollTarget - sectionSeparator - marginBottom
        ) {
          panelRef.current.style.position = 'absolute';
          panelRef.current.style.top = `${
            topOfScrollTarget -
            sectionSeparator -
            marginBottom -
            panelHeight -
            topOfStartTarget -
            padding
          }px`;
          panelRef.current.style.width = `${startWidth}px`;
          panelRef.current.style.left = `0px`;
        }
      }
    };

    const handleResize = () => {
      // here the positions of the starting and ending targets are recalculated
      topOfStartTarget = startTargetRef.current.getBoundingClientRect().top + window.scrollY;
      topOfScrollTarget = scrollTargetRef.current.getBoundingClientRect().top + window.scrollY;
    };

    // check if the height of the panel is bigger than the viewport height
    // don't scroll it when it is

    if (panel && exceedsViewport(panel.offsetHeight, window.innerHeight, bottomNavHeight)) {
      return;
    }

    window.addEventListener('scroll', repositionPanel);
    window.addEventListener('resize', handleResize);
    // A layout change (rotation) may not fire a scroll event, so position the panel right away
    repositionPanel();
    return () => {
      window.removeEventListener('scroll', repositionPanel);
      window.removeEventListener('resize', handleResize);
      // The panel only follows the scroll in the desktop layout; hand it back to the stylesheet
      if (panel) resetPanelStyles(panel);
    };
  }, [
    panelRef,
    startTargetRef,
    scrollTargetRef,
    isDesktopLayout,
    topbarOffset,
    bottomNavHeight,
  ]);

  const form = showBookingDatesForm ? (
    <BookingDatesForm
      annualPassDateOverlapError={bookingForm.annualPassDateOverlapError}
      bookingDates={bookingForm.bookingDates}
      bookingLimit={bookingLimit}
      control={bookingForm.control}
      countLabels={bookingForm.countLabels}
      currentListing={currentListing}
      effectiveAnnualPassError={bookingForm.effectiveAnnualPassError}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
      guestOptions={bookingForm.guestOptions}
      minimumNights={minimumNights}
      onFetchTimeSlots={onFetchTimeSlots}
      onOpenCheckout={openCheckout}
      showAnnualPassError={showAnnualPassError}
      timeSlots={timeSlots}
    />
  ) : null;

  return (
    <>
      <BookingPanelFrame isTablet={isTablet} panelRef={panelRef} variant={variant}>
        <BookingPanelContent
          authorDisplayName={authorDisplayName}
          category={category}
          checkoutOpen={checkoutOpen}
          currentListing={currentListing}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          form={form}
          formattedPrice={formattedPrice}
          hasDatesSelected={bookingForm.hasDatesSelected}
          isClosed={isClosed}
          lineItems={lineItems}
          modalVariant={modalVariant}
          onCloseSheet={closeSheet}
          onOpenSheet={openSheet}
          priceTitle={priceTitle}
          sheetOpen={sheetOpen}
          showAnnualPassError={showAnnualPassError}
          showBookingDatesForm={showBookingDatesForm}
          title={title}
          variant={variant}
        />
      </BookingPanelFrame>

      {/* Outside the layout frame, so the checkout keeps its step and inputs across a layout switch */}
      {showBookingDatesForm ? (
        <BookingModal
          bookingDates={bookingForm.bookingDates}
          bookingLimit={bookingLimit}
          categoryLabel={bookingForm.categoryLabel}
          currentListing={currentListing}
          distanceToLocation={distanceToLocation}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
          getGuestDisplayValue={bookingForm.getGuestDisplayValue}
          guestCount={bookingForm.guestCount}
          guestOptions={bookingForm.guestOptions}
          lineItems={lineItems}
          minimumNights={minimumNights}
          onBookingDatesChange={bookingForm.setBookingDates}
          onClose={closeCheckout}
          onGuestCountChange={bookingForm.setGuestCount}
          onMonthChange={onMonthChange}
          onSubmit={bookingForm.submit}
          open={checkoutOpen}
          showAnnualPassError={bookingForm.effectiveAnnualPassError}
          steps={bookingForm.steps}
          timeSlots={timeSlots}
        />
      ) : null}

      <SectionShareModal
        currentListing={currentListing}
        onShareListing={onShareListing}
        open={openShareModal}
      />
    </>
  );
};

BookingPanelComponent.displayName = 'BookingPanel';

const BookingPanel = memo(BookingPanelComponent);

export default BookingPanel;
